<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import {
  collectionList,
  collectionAdd,
  collectionCopy,
  collectionDel,
} from "@/common/api/rest/collection";

import type {
  CollectionType,
  CollectionPayloadType,
} from "@/common/types/collection";

import { FormCollection } from "@/components";
import {
  baseAvatar,
  breadcrumbSimple,
  baseConfirm,
  baseModal,
  baseTable,
  svgIcon,
} from "@/common/components";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const tableData = ref<CollectionType[]>([]);
const payload = ref<CollectionPayloadType | null>(null);

const selected = ref<CollectionType | null>(null);

const limit = ref<number>(10);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const showModalAdd = ref(false);
const showModalCopy = ref(false);
const showModalDel = ref(false);
const payloadValid = ref(false);

const type = computed(() => route.params.type as string);

const labelTypes = computed(() => ({
  console: t(`collection.labels.consoles`),
  game: t(`collection.labels.games`),
  accessorie: t(`collection.labels.accessories`),
}));

const tableHeader = computed(() => [
  { key: "avatar", label: "" },
  { key: "name", label: t("common.labels.name") },
  { key: "status", label: t("common.labels.status") },
  { key: "is_main", label: t("collection.labels.is_main") },
  { key: "is_official", label: t("collection.labels.is_official") },
]);

const breadcrumbList = computed(() => [
  {
    key: "collection",
    label: t("common.labels.collections"),
    url: `/collection`,
  },
  {
    key: "edit",
    label: labelTypes.value[type.value],
  },
]);

function fetchData() {
  collectionList({
    page: page.value,
    limit: limit.value,
    search: search.value,
    type: type.value,
    status: "ALL",
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function changePage(value: number) {
  page.value = value;
  fetchData();
}

function searchItem(value: string) {
  search.value = value;

  fetchData();
}

function addItem() {
  showModalAdd.value = true;
}

function addItemCancel() {
  showModalAdd.value = false;
  payload.value = null;
}

function addItemConfirm() {
  if (!payload.value) return;

  store.commit("SET_LOADING", true);

  collectionAdd(payload.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        editItem(data);
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function editItem(data: CollectionType) {
  router.push(`/collection/${type.value}/${data.collection_id}/infos`);
}

function copyItem(data: CollectionType) {
  selected.value = data;
  showModalCopy.value = true;
}

function copyItemCancel() {
  showModalCopy.value = false;
  selected.value = null;
}

function copyItemConfirm() {
  if (!selected.value) return;

  store.commit("SET_LOADING", true);

  collectionCopy(selected.value.collection_id)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        editItem(data);
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function removeItem(data: CollectionType) {
  selected.value = data;
  showModalDel.value = true;
}

function removeItemCancel() {
  showModalDel.value = false;
  selected.value = null;
}

function removeItemConfirm() {
  if (!selected.value) return;

  collectionDel(selected.value.collection_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        removeItemCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <article class="collection-page">
    <section class="page-header">
      <breadcrumb-simple
        :items="breadcrumbList"
        :show-home="true"
        active="edit"
        url-home="/home"
      />
    </section>

    <section class="page-content">
      <div class="tab-header">
        <svg-icon icon="gamepad" size="2rem" />
        {{ labelTypes[type] }}
      </div>

      <div class="tab-content">
        <base-table
          :header="tableHeader"
          :data="tableData"
          :limit="limit"
          :page="page"
          :pages="pages"
          :total="total"
          :searched="searched"
          :options="true"
          :copy="true"
          :edit="true"
          :del="true"
          @change:page="changePage($event)"
          @searching="searchItem($event)"
          @btn:add="addItem()"
          @btn:copy="copyItem($event)"
          @btn:edit="editItem($event)"
          @btn:del="removeItem($event)"
        >
          <template #avatar="{ data }">
            <base-avatar
              position="center"
              :is-edit="false"
              :show-image="true"
              :url="data?.avatar?.large"
              :aspect-ratio="1.333"
              size="3rem"
              border-radius="0.25rem"
            />
          </template>
          <template #status="{ data }">
            <template v-if="data.status === 'ACTIVE'">
              {{ t("common.labels.active") }}
            </template>
            <template v-else>
              {{ t("common.labels.inactive") }}
            </template>
          </template>
          <template #is_main="{ data }">
            <template v-if="data.is_main">
              {{ t("common.labels.yes") }}
            </template>
            <template v-else>
              {{ t("common.labels.not") }}
            </template>
          </template>
          <template #is_official="{ data }">
            <template v-if="data.is_official">
              {{ t("common.labels.yes") }}
            </template>
            <template v-else>
              {{ t("common.labels.not") }}
            </template>
          </template>
        </base-table>
      </div>
    </section>
  </article>

  <base-modal
    :show="showModalAdd"
    :title="t('collection.modals.titles.add_collection').toUpperCase()"
    :show-button-confirm="true"
    :button-confirm="t('common.labels.save')"
    :button-close="t('common.labels.cancel')"
    @close="addItemCancel()"
    @confirm="addItemConfirm()"
  >
    <template #content>
      <form-collection
        :type="type"
        @update:data="payload = $event"
        @validate="payloadValid = $event"
      />
    </template>
  </base-modal>

  <base-confirm
    :show="showModalCopy"
    :title="t('collection.modals.titles.copy_collection').toUpperCase()"
    :message="
      t('collection.modals.messages.copy_collection', {
        name: selected?.name?.toUpperCase(),
      })
    "
    @cancel="copyItemCancel()"
    @confirm="copyItemConfirm()"
  />

  <base-confirm
    :show="showModalDel"
    :title="t('collection.modals.titles.del_collection').toUpperCase()"
    :message="
      t('collection.modals.messages.del_collection', {
        name: selected?.name?.toUpperCase(),
      })
    "
    @cancel="removeItemCancel()"
    @confirm="removeItemConfirm()"
  />
</template>

<style lang="scss" scoped>
.collection-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .page-header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: inherit;
    background: white;
    border-radius: 0.5rem;
    padding-inline: 1rem;
    padding-block: 0.5rem;
  }

  .page-content {
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 0.5rem;

    .tab-header {
      display: inline-flex;
      gap: 0.5rem;
      font-size: 1.2rem;
      align-items: center;
      padding-inline: 1.25rem;
      padding-block: 0.75rem;
      border-block-end: 1px solid var(--border);
      text-transform: uppercase;
    }

    .tab-content {
      display: flex;
      flex-direction: column;
      padding-inline: 1.5rem;
      padding-block: 1rem;
      gap: 1rem;

      .avatar {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        width: 3rem;
        height: 3rem;
        border-radius: 0.5rem;
        background: #fff;
        padding: 0.5rem;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }
    }
  }
}
</style>
